import { getRoot } from './url.js';

/** Known feature → relative script path under the core script directory. */
export const FEATURE_SCRIPTS = {
  record: './features/record.iife.min.js',
};

/**
 * Ensure CstPrum exists on the global and return it.
 * @returns {Record<string, unknown>}
 */
export function ensureApi() {
  const root = getRoot();
  if (!root) {
    return {};
  }
  if (!root.CstPrum || typeof root.CstPrum !== 'object') {
    root.CstPrum = {};
  }
  return root.CstPrum;
}

/**
 * Register a feature export onto window.CstPrum.
 * Used by feature IIFE chunks (e.g. record attaches `record`).
 *
 * @param {string} name
 * @param {unknown} value
 */
export function registerFeature(name, value) {
  const api = ensureApi();
  api[name] = value;
}

/**
 * Load a script by URL once. Resolves on load; rejects on error.
 * @param {string} src
 * @returns {Promise<void>}
 */
export function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('document is not available'));
      return;
    }

    const existing = document.querySelector(`script[data-cst-prum-feature="${src}"]`);
    if (existing) {
      if (existing.getAttribute('data-cst-prum-loaded') === '1') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error(`Failed to load feature script: ${src}`)),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.setAttribute('data-cst-prum-feature', src);
    script.onload = () => {
      script.setAttribute('data-cst-prum-loaded', '1');
      resolve();
    };
    script.onerror = () => {
      reject(new Error(`Failed to load feature script: ${src}`));
    };
    (document.head || document.documentElement).appendChild(script);
  });
}

/**
 * Resolve a relative feature path against the core script base URL.
 * @param {string} baseUrl directory URL ending with /
 * @param {string} relativePath e.g. ./features/record.iife.min.js
 * @returns {string}
 */
export function resolveFeatureUrl(baseUrl, relativePath) {
  try {
    return new URL(relativePath, baseUrl).href;
  } catch {
    return `${baseUrl.replace(/\/?$/, '/')}${relativePath.replace(/^\.\//, '')}`;
  }
}

/**
 * Load every enabled known feature. Unknown manifest keys are ignored.
 * Failures are soft (logged when console is available).
 *
 * @param {Record<string, boolean>} features
 * @param {string} baseUrl
 * @returns {Promise<void>}
 */
export async function loadEnabledFeatures(features, baseUrl) {
  if (!features || !baseUrl) return;

  const loads = [];

  for (const [name, enabled] of Object.entries(features)) {
    if (!enabled) continue;
    const relative = FEATURE_SCRIPTS[name];
    if (!relative) continue;

    const src = resolveFeatureUrl(baseUrl, relative);
    loads.push(
      loadScript(src).catch((err) => {
        if (typeof console !== 'undefined' && console.warn) {
          console.warn(`[CstPrum] Feature "${name}" failed to load:`, err);
        }
      }),
    );
  }

  await Promise.all(loads);
}
